"use client";

/**
 * Adresowanie kroków publicznej strony zamawiania usługi w URL.
 *
 * Ten sam mechanizm co w pluginie panelu klienta (`plugins/CCrmProjectManagement/taskRoute.ts`):
 * stan jedzie w hashu, rozdzielonym PRZECINKAMI (wzorzec ILINK dla anchor), a człon należący
 * do tej strony ma własny prefiks, więc człony obce zostają nietknięte.
 *
 * | Widok                        | Hash              |
 * |------------------------------|-------------------|
 * | oferta pakietów              | brak / `#`        |
 * | formularz zamówienia pakietu 12 | `#order-12`    |
 *
 * Dzięki temu formularz konkretnego pakietu ma własny adres: da się go skopiować, wysłać
 * klientowi, wrzucić do kampanii albo podlinkować z cennika, a przyciski Wstecz/Dalej
 * przeglądarki działają bez dodatkowego kodu.
 *
 * Nawigacja idzie przez `location.hash`, a NIE przez router Next.js - zmiana hasha nie
 * przeładowuje Server Components, więc przejście do formularza nie renderuje strony od nowa.
 *
 * Link do nieistniejącego albo zdjętego z oferty pakietu nie jest tu problemem: dostępność
 * i tak weryfikuje backend przy pobraniu formularza (2260044), więc taki adres kończy się
 * czytelnym komunikatem, a nie podglądem czegoś, czego nie ma w ofercie.
 */

/** Prefiks członu hasha należącego do tej strony. */
const PIECE_PREFIX = "order-";

/** `order-{idPakietu}` */
const PIECE_RE = /^order-(\d+)$/;

/**
 * Odczytuje ID zamawianego pakietu z hasha adresu.
 *
 * @param hash Wartość `location.hash` (z `#` albo bez)
 * @returns ID pakietu albo `null` dla widoku oferty
 */
export function parseOrderRoute(hash: string): number | null {
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;

  for (const piece of stripped.split(",")) {
    const match = PIECE_RE.exec(piece);
    if (!match) continue;

    const id = Number(match[1]);
    if (Number.isFinite(id) && id > 0) return id;
  }

  return null;
}

/**
 * Buduje pełny hash dla widoku, ZACHOWUJĄC człony obce - podmieniany jest wyłącznie
 * człon `order-`.
 *
 * @param currentHash Bieżący `location.hash`
 * @param idPackage   ID pakietu albo `null` dla powrotu do oferty
 */
export function buildOrderHash(currentHash: string, idPackage: number | null): string {
  const stripped = currentHash.startsWith("#") ? currentHash.slice(1) : currentHash;

  const kept = stripped
    .split(",")
    .filter((piece) => piece !== "" && !piece.startsWith(PIECE_PREFIX));

  if (idPackage !== null) kept.push(`${PIECE_PREFIX}${idPackage}`);

  return kept.length > 0 ? `#${kept.join(",")}` : "#";
}

/**
 * Przechodzi do widoku przez zmianę hasha (wpis w historii przeglądarki).
 *
 * @param idPackage ID pakietu albo `null` dla powrotu do oferty
 */
export function navigateToOrder(idPackage: number | null): void {
  if (typeof window === "undefined") return;
  window.location.hash = buildOrderHash(window.location.hash, idPackage);
}
